import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows sliding between content like a carousel.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #touchStartX = 0;
  #touchStartY = 0;
  #touchCurrentX = 0;
  #isDragging = false;
  #startIndex = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#handleResize);

    // Touch event listeners
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (slidesContainer) {
      slidesContainer.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      slidesContainer.addEventListener('touchmove', this.#handleTouchMove, { passive: true });
      slidesContainer.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    }

    // Set wrapper width based on number of slides
    this.#updateWrapperWidth();

    // Initialize carousel position
    this.current = 0;

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (slidesContainer) {
      slidesContainer.removeEventListener('touchstart', this.#handleTouchStart);
      slidesContainer.removeEventListener('touchmove', this.#handleTouchMove);
      slidesContainer.removeEventListener('touchend', this.#handleTouchEnd);
    }
  }

  #handleResize = () => {
    this.#updateWrapperWidth();
    // Update position after resize to ensure correct alignment
    this.current = this.#current;
  }

  #updateWrapperWidth() {
    const slidesWrapper = this.querySelector('.announcement-bar__slides-wrapper');
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    
    if (slidesWrapper && slidesContainer) {
      const slides = Array.from(slidesWrapper.children);
      if (slides.length === 0) return;

      const containerWidth = slidesContainer.offsetWidth;
      const totalWidth = containerWidth * slides.length;
      slidesWrapper.style.width = `${totalWidth}px`;
      
      // Ensure each slide is exactly the container width
      slides.forEach(slide => {
        slide.style.width = `${containerWidth}px`;
        slide.style.flexShrink = '0';
      });
    }
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slidesWrapper = this.querySelector('.announcement-bar__slides-wrapper');
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    
    if (!slidesWrapper || !slidesContainer) return;

    const slides = Array.from(slidesWrapper.children);
    if (slides.length === 0) return;

    const slideCount = slides.length;
    const containerWidth = slidesContainer.offsetWidth;
    
    // Use modulo to wrap indices (normal carousel behavior)
    let targetIndex = current % slideCount;
    if (targetIndex < 0) {
      targetIndex += slideCount;
    }

    this.#current = current;
    const translateX = -targetIndex * containerWidth;
    
    // Enable smooth sliding animation
    slidesWrapper.style.transition = 'transform 0.5s ease-in-out';
    slidesWrapper.style.transform = `translateX(${translateX}px)`;

    // Update aria-hidden for all slides
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== targetIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #handleTouchStart = (event) => {
    if (event.touches.length !== 1) return;
    
    const touch = event.touches[0];
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
    this.#touchCurrentX = touch.clientX;
    this.#isDragging = true;
    
    // Get the wrapped index for the starting position
    const slidesWrapper = this.querySelector('.announcement-bar__slides-wrapper');
    if (!slidesWrapper) return;
    
    const slides = Array.from(slidesWrapper.children);
    if (slides.length === 0) return;
    
    const slideCount = slides.length;
    let wrappedIndex = this.#current % slideCount;
    if (wrappedIndex < 0) {
      wrappedIndex += slideCount;
    }
    this.#startIndex = wrappedIndex;
    
    // Pause autoplay during drag
    this.suspend();
    
    // Disable transition for smooth dragging
    if (slidesWrapper) {
      slidesWrapper.style.transition = 'none';
    }
  };

  #handleTouchMove = (event) => {
    if (!this.#isDragging || event.touches.length !== 1) return;
    
    const touch = event.touches[0];
    this.#touchCurrentX = touch.clientX;
    
    const slidesWrapper = this.querySelector('.announcement-bar__slides-wrapper');
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    
    if (!slidesWrapper || !slidesContainer) return;

    const slides = Array.from(slidesWrapper.children);
    if (slides.length === 0) return;

    const slideCount = slides.length;
    const containerWidth = slidesContainer.offsetWidth;
    
    // Calculate drag distance
    const deltaX = this.#touchCurrentX - this.#touchStartX;
    
    // Calculate current position based on start index and drag
    const baseTranslateX = -this.#startIndex * containerWidth;
    const dragTranslateX = deltaX;
    let totalTranslateX = baseTranslateX + dragTranslateX;
    
    // Constrain movement to prevent going too far (optional - allows some resistance at edges)
    const minTranslateX = -(slideCount - 1) * containerWidth;
    const maxTranslateX = 0;
    
    // Apply slight resistance at edges but don't completely block
    if (totalTranslateX > maxTranslateX) {
      totalTranslateX = maxTranslateX + (totalTranslateX - maxTranslateX) * 0.3;
    } else if (totalTranslateX < minTranslateX) {
      totalTranslateX = minTranslateX + (totalTranslateX - minTranslateX) * 0.3;
    }
    
    // Apply the drag transform
    slidesWrapper.style.transform = `translateX(${totalTranslateX}px)`;
  };

  #handleTouchEnd = (event) => {
    if (!this.#isDragging) return;
    
    this.#isDragging = false;
    
    const slidesWrapper = this.querySelector('.announcement-bar__slides-wrapper');
    
    if (!slidesWrapper) return;
    
    // Calculate swipe distance and direction
    const deltaX = this.#touchCurrentX - this.#touchStartX;
    const deltaY = Math.abs(event.changedTouches[0].clientY - this.#touchStartY);
    const minSwipeDistance = 50; // Minimum distance for a swipe
    const maxVerticalDistance = 30; // Maximum vertical movement to consider it a horizontal swipe
    
    // Re-enable transition
    slidesWrapper.style.transition = 'transform 0.5s ease-in-out';
    
    // Determine if it's a valid horizontal swipe
    if (Math.abs(deltaX) > minSwipeDistance && deltaY < maxVerticalDistance) {
      if (deltaX > 0) {
        // Swipe right - go to previous slide
        this.previous();
      } else {
        // Swipe left - go to next slide
        this.next();
      }
    } else {
      // Not a valid swipe, return to current slide
      this.current = this.#current;
    }
    
    // Resume autoplay
    if (this.autoplay && !this.paused) {
      this.play();
    }
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
